import React, { useState } from 'react'
import { Input, Typography, theme } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import GenAIActionButtons from './GenAIActionButtons'
import GenAIGenerating from './GenAIGenerating'
import TypingAnimation from '../TypingAnimation'

const { Text, Paragraph } = Typography
const { TextArea } = Input

const GenAIContent = ({ tab, onGenerate, onTypingComplete, onContentChange, onInsertToNote, bookingData, userRef }) => {
  const { token } = theme.useToken()
  const [query, setQuery] = useState('')

  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return ''
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const handleGenerate = () => {
    if (!query.trim() || tab.isGenerating) return
    onGenerate(tab.id, query)
    setQuery('') // Clear input after generation
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !tab.isGenerating && query.trim()) {
      handleGenerate()
    }
  }

  const handleInputChange = (e) => {
    setQuery(e.target.value)
  }

  const handleContentEdit = (e) => {
    if (onContentChange) {
      onContentChange(tab.id, e.target.value)
    }
  }

  const handleTypingAnimationComplete = () => {
    onTypingComplete(tab.id)
  }

  const renderContent = () => {
    // Show nothing when generating (let GenAIGenerating component handle the display)
    if (tab.isGenerating) {
      return null
    }

    // Show typing animation
    if (tab.isTyping) {
      return (
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '16px',
            fontSize: '18px',
            lineHeight: '1.6',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          <TypingAnimation text={tab.rawAiResponse} speed={3} onComplete={handleTypingAnimationComplete} />
        </div>
      )
    }

    // Show generated content with action buttons
    if (tab.generatedContent) {
      return (
        <>
          {/* Action Buttons */}
          <GenAIActionButtons
            content={stripHtml(tab.generatedContent)}
            onInsertToNote={onInsertToNote}
            bookingData={bookingData}
            userRef={userRef}
          />

          {/* Generated Content */}
          <div style={{ flex: 1, overflow: 'visible', padding: '16px' }}>
            <TextArea
              value={stripHtml(tab.generatedContent)}
              onChange={handleContentEdit}
              autoSize={true}
              style={{
                fontSize: '18px',
                lineHeight: '1.6',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                border: 'none',
                padding: 0,
                boxShadow: 'none',
                resize: 'none',
                background: 'transparent',
                width: '100%'
              }}
            />
          </div>
        </>
      )
    }

    // Default state (empty)
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '350px',
          textAlign: 'center',
          padding: '16px'
        }}
      >
        <Text type='secondary' style={{ fontSize: '16px', marginBottom: '16px' }}>
          No content generated yet
        </Text>
        <Paragraph type='secondary' style={{ maxWidth: '400px' }}>
          Use the template selector above to generate content, or type a custom query to create AI-generated content.
        </Paragraph>
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'white'
      }}
    >
      {/* Show query input only if we're not showing template selector and don't have generated content */}
      {!tab.showingTemplateSelector && !tab.generatedContent && (
        <div style={{ padding: '16px', borderBottom: `1px solid ${token.colorBorder}` }}>
          <div style={{ position: 'relative' }}>
            <Input
              placeholder='Ask AI to generate content...'
              value={query}
              onChange={handleInputChange}
              onPressEnter={handleKeyPress}
              disabled={tab.isGenerating}
              style={{
                borderRadius: '24px',
                fontSize: '16px',
                padding: '12px 48px 12px 16px',
                border: `2px solid ${token.colorBorder}`,
                backgroundColor: '#fafafa'
              }}
              suffix={
                query?.trim() && !tab.isGenerating ? (
                  <div
                    onClick={handleGenerate}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: token.colorPrimary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = token.colorPrimaryHover
                      e.target.style.transform = 'translateY(-50%) scale(1.1)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = token.colorPrimary
                      e.target.style.transform = 'translateY(-50%) scale(1)'
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faArrowRight}
                      style={{
                        color: 'white',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                ) : null
              }
            />
          </div>
        </div>
      )}

      {/* Generating Indicator */}
      {tab.isGenerating && <GenAIGenerating />}

      {/* Content Area */}
      {renderContent()}
    </div>
  )
}

export default GenAIContent
